import React, { useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowLeft, Shield, Eye, Database, Trash2, Lock, Users, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">{t('privacyPolicy.back')}</span>
            </button>
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">{t('privacyPolicy.title')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-xl p-8">

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 text-blue-600 mr-3" />
              {t('privacyPolicy.introduction.title')}
            </h2>
            <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-0">
                  {t('privacyPolicy.introduction.content')}
                </p>
              </div>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 text-green-600 mr-3" />
              {t('privacyPolicy.informationWeCollect.title')}
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('privacyPolicy.informationWeCollect.personalInfo.title')}</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>{t('privacyPolicy.informationWeCollect.personalInfo.email')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.personalInfo.name')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.personalInfo.profile')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.personalInfo.payment')}</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('privacyPolicy.informationWeCollect.images.title')}</h3>
                <p className="text-gray-700 mb-3">{t('privacyPolicy.informationWeCollect.images.description')}</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>{t('privacyPolicy.informationWeCollect.images.selfies')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.images.garments')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.images.generated')}</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('privacyPolicy.informationWeCollect.usage.title')}</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>{t('privacyPolicy.informationWeCollect.usage.device')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.usage.browser')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.usage.analytics')}</li>
                  <li>{t('privacyPolicy.informationWeCollect.usage.cookies')}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 text-purple-600 mr-3" />
              {t('privacyPolicy.howWeUse.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.howWeUse.virtualTryOn')}</h3>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.howWeUse.account')}</h3>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.howWeUse.communication')}</h3>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.howWeUse.improvement')}</h3>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.howWeUse.security')}</h3>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.howWeUse.legal')}</h3>
              </div>
            </div>
          </section>

          {/* Image Storage and Deletion */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Trash2 className="h-6 w-6 text-red-600 mr-3" />
              {t('privacyPolicy.imageStorage.title')}
            </h2>
            <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
              <h3 className="text-lg font-semibold text-red-800 mb-3">{t('privacyPolicy.imageStorage.important')}</h3>
              <p className="text-red-700 mb-4">{t('privacyPolicy.imageStorage.description')}</p>
              <ul className="list-disc list-inside space-y-2 text-red-700">
                <li>{t('privacyPolicy.imageStorage.permanent')}</li>
                <li>{t('privacyPolicy.imageStorage.deletion')}</li>
                <li>{t('privacyPolicy.imageStorage.retention')}</li>
                <li>{t('privacyPolicy.imageStorage.backup')}</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 text-indigo-600 mr-3" />
              {t('privacyPolicy.dataSecurity.title')}
            </h2>
            <div className="bg-indigo-50 rounded-lg p-6 border-l-4 border-indigo-500 mb-6">
              <p className="text-gray-700 mb-0">{t('privacyPolicy.dataSecurity.description')}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-indigo-50 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-800 mb-2">{t('privacyPolicy.dataSecurity.encryption')}</h3>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-800 mb-2">{t('privacyPolicy.dataSecurity.access')}</h3>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-800 mb-2">{t('privacyPolicy.dataSecurity.monitoring')}</h3>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-800 mb-2">{t('privacyPolicy.dataSecurity.compliance')}</h3>
              </div>
            </div>
          </section>


          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.yourRights.title')}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">{t('privacyPolicy.yourRights.access.title')}</h3>
                  <p className="text-sm text-blue-700">{t('privacyPolicy.yourRights.access.description')}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 mb-2">{t('privacyPolicy.yourRights.rectification.title')}</h3>
                  <p className="text-sm text-green-700">{t('privacyPolicy.yourRights.rectification.description')}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-2">{t('privacyPolicy.yourRights.deletion.title')}</h3>
                  <p className="text-sm text-red-700">{t('privacyPolicy.yourRights.deletion.description')}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-800 mb-2">{t('privacyPolicy.yourRights.portability.title')}</h3>
                  <p className="text-sm text-purple-700">{t('privacyPolicy.yourRights.portability.description')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Mail className="h-6 w-6 text-gray-600 mr-3" />
              {t('privacyPolicy.contact.title')}
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">{t('privacyPolicy.contact.description')}</p>
              <div className="space-y-2">
                <p className="text-gray-700">
                  <strong>Email:</strong> contact@luxify.com
                </p>
                <p className="text-gray-700">
                  <strong>Dirección:</strong> Córdoba, Argentina
                </p>
              </div>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.changes.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('privacyPolicy.changes.description')}
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              {t('privacyPolicy.footer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
